/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { Button } from "@/components/ui/button";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { cn } from "@/lib/utils";
import { Check, Copy } from "lucide-react";

interface CopyButtonProps {
  text: string;
  className?: string;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  size?: "default" | "sm" | "lg" | "icon";
}

export const CopyButton = ({
  text,
  className,
  variant = "default",
  size = "default",
}: CopyButtonProps) => {
  const { copied, copy } = useCopyToClipboard();

  return (
    <Button
      variant={variant}
      size={size}
      className={cn("gap-1", className)}
      onClick={() => copy(text)}
      disabled={copied}
      title={copied ? "Copied!" : "Copy to clipboard"}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          {size !== "sm" && "Copied!"}
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          {size !== "sm" && "Copy"}
        </>
      )}
    </Button>
  );
};
